import { Box, Select, MenuItem } from '@mui/material'
import {
  BatchSetting,
  ProductSetting,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import {
  processRowUpdate,
  useSettingsContext,
} from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { toast } from 'react-hot-toast'
import {
  useUpdateSettings,
  useUploadProductSettings,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.data'

type ProductSettingsTableProps = {
  data: ProductSetting[]
  isLoading?: boolean
  batchData: BatchSetting[]
}

const onSuccess = () => {
  toast.success('Update Successful')
}

const onError = () => {
  toast.error('Update Failed')
}

export const ProductSettingsTable = ({
  data,
  isLoading,
  batchData,
}: ProductSettingsTableProps) => {
  const { mutate, isPending: isSaving } = useUpdateSettings(onSuccess, onError)
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadProductSettings(
      () => {
        toast.success('Upload Successful')
      },
      () => {
        toast.error('Upload Failed')
      }
    )

  const COLUMN_WIDTH = 150

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'workcentreCode',
        headerName: 'Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'skuGroupCode',
        headerName: 'SKU Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'skuGroupName',
        headerName: 'SKU Group Name',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'productFormat',
        headerName: 'Product Format',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'batchGroupingCode',
        headerName: 'Batch Group Code', // Updated display name
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'unitOfMeasureCode',
        headerName: 'Unit Of Measure Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'skuSalesRate',
        headerName: 'Sales Rate',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          return value === null ? '' : value.toFixed(2)
        },
      },
      {
        field: 'workCentreAllocation',
        headerName: 'Work Centre Allocation',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          return value === null ? '' : `${value * 100}%`
        },
      },
      {
        field: 'allocatedSalesRate',
        headerName: 'Allocated Sales Rate',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          return value === null ? '' : value.toFixed(2)
        },
      },
      {
        field: 'minimumProductionOrderQuantity',
        headerName: 'Min Production Order Quantity',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
      },
      {
        field: 'naturalCycle',
        headerName: 'Natural Cycle',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          if (value === null) return ''
          if (value === Infinity) return '-'
          return value.toFixed(3)
        },
        renderCell: (params) => {
          if (params?.value === Infinity) {
            return '-'
          }
          return params.formattedValue
        },
      },
      {
        field: 'initialCycle',
        headerName: 'Initial Cycle',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'minBatchCycleInput',
        headerName: 'Min Batch Cycle Input',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'minCycleOverride',
        headerName: 'Min Cycle Override',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        renderEditCell: (params) => {
          return (
            <Select
              value={params.value || ''}
              onChange={async (event) => {
                const newValue = event.target.value
                const isValid = await params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: newValue,
                })

                if (isValid) {
                  const cellMode = params.api.getCellMode(
                    params.id,
                    params.field
                  )
                  if (cellMode === 'edit') {
                    params.api.stopCellEditMode({
                      id: params.id,
                      field: params.field,
                    })
                  }
                }
              }}
              onClose={(_event) => {
                const cellMode = params.api.getCellMode(params.id, params.field)
                if (cellMode === 'edit') {
                  params.api.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                  })
                }
              }}
              sx={{
                width: '100%',
                minWidth: COLUMN_WIDTH,
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              size="small"
              open={true}
              variant="outlined"
            >
              {params.colDef.valueOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        },
      },
      {
        field: 'maxCycleOverride',
        headerName: 'Max Cycle Override',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        renderEditCell: (params) => {
          return (
            <Select
              value={params.value || ''}
              onChange={async (event) => {
                const newValue = event.target.value
                const isValid = await params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: newValue,
                })

                if (isValid) {
                  const cellMode = params.api.getCellMode(
                    params.id,
                    params.field
                  )
                  if (cellMode === 'edit') {
                    params.api.stopCellEditMode({
                      id: params.id,
                      field: params.field,
                    })
                  }
                }
              }}
              onClose={(_event) => {
                const cellMode = params.api.getCellMode(params.id, params.field)
                if (cellMode === 'edit') {
                  params.api.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                  })
                }
              }}
              sx={{
                width: '100%',
                minWidth: COLUMN_WIDTH,
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              size="small"
              open={true}
              variant="outlined"
            >
              {params.colDef.valueOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        },
      },
      {
        field: 'minCycleInput',
        headerName: 'Min Cycle Input',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'maxCycleInput',
        headerName: 'Max Cycle Input',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'isOverriden',
        headerName: 'Is Overriden', // Matching display name from config
        width: COLUMN_WIDTH,
        type: 'boolean',
      },
      {
        field: 'runRate',
        headerName: 'Run Rate',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value: number) => {
          return value === null ? '' : value.toFixed(2)
        },
      },

      {
        field: 'isIncluded',
        headerName: 'Is Included',
        width: COLUMN_WIDTH,
        type: 'boolean',
      },

      {
        field: 'isPrimaryWorkCentre',
        headerName: 'Is Primary Work Centre',
        width: COLUMN_WIDTH,
        type: 'boolean', // Updated to boolean from string
      },
    ]
  }, [])

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state

  const updateTableData = (changedRows: Record<number, ProductSetting>) => {
    mutate({ changedRows, projectId, scenarioId })
  }

  const uploadData = (file: File) => {
    uploadMutate({ file, projectId, scenarioId })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading ?? true}
        updateTableData={updateTableData}
        processRow={processRowUpdate(batchData)}
        uploadData={uploadData}
        showTableToolBar={true}
        isUploading={isUploading ?? false}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={['baseSourceSiteCode', 'workcentreCode', 'skuGroupCode']}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
